import { maybeLocalMobileUrl } from '@/local_dev/maybe-local-mobile-url';
import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from '@/shared/cell-session';
import { withTokenAuthRecovery } from '@/shared/cors-auth-recovery';

// =============================================================================
// mobile-service direct-CORS client
// =============================================================================
//
// Browser-side calls to ``https://{cellId}.api.tranquility.ai/mobile-service/v1/*``
// for the standalone ``/mobile-viewer``. Mirrors ``cors-junkdrawer-api.ts``:
// the access token comes from ``useCellServiceSession`` (the real Cognito
// access token, NOT the ID token) and every request carries
// ``Authorization: Bearer {accessToken}``. There is no Next.js BFF proxy —
// requests go straight to the cell API gateway.
//
// mobile-service exposes three GraphQL endpoints selected by acquisition
// vendor plus a REST surface for acquisitions and the UI-config document.

/** Path prefix for every mobile-service v1 endpoint. */
const API_PREFIX = '/mobile-service/v1';

/** Wall-clock limit for a single mobile-service request. A timeout throws; it is not retried. */
export const MOBILE_REQUEST_TIMEOUT_MS = 60_000;

/** GraphQL endpoint scope; picks the route under ``/mobile-service/v1``. */
export type MobileGraphqlEndpoint = 'mobile' | 'cellebrite' | 'magnet';

const GRAPHQL_PATH_BY_ENDPOINT: Record<MobileGraphqlEndpoint, string> = {
  mobile: '/graphql',
  cellebrite: '/cellebrite/graphql',
  magnet: '/magnet/graphql',
};

/**
 * Error thrown by every direct-CORS call to mobile-service. Carries the
 * upstream HTTP status (when available) and the raw response body.
 */
export class MobileApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'MobileApiError';
    this.status = status;
    this.detail = detail;
  }
}

export class MobileTimeoutError extends MobileApiError {
  constructor(message: string) {
    super(message);
    this.name = 'MobileTimeoutError';
  }
}

/** User-visible copy for mobile-service failures — never surface upstream ``detail``. */
export const MOBILE_USER_FACING_ERROR_MESSAGE = 'Error loading mobile forensics data';

export function mobileUserFacingErrorMessage(_error?: unknown): string {
  return MOBILE_USER_FACING_ERROR_MESSAGE;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

type TimedFetchSignal = {
  signal: AbortSignal;
  cleanup: () => void;
  wasTimedOut: () => boolean;
};

/** Merge an optional caller ``AbortSignal`` with a single-attempt timeout. */
function createTimedFetchSignal(
  outerSignal?: AbortSignal,
  timeoutMs: number = MOBILE_REQUEST_TIMEOUT_MS,
): TimedFetchSignal {
  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onOuterAbort = () => controller.abort();
  if (outerSignal?.aborted) {
    controller.abort();
  } else {
    outerSignal?.addEventListener('abort', onOuterAbort, { once: true });
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      outerSignal?.removeEventListener('abort', onOuterAbort);
    },
    wasTimedOut: () => timedOut && !outerSignal?.aborted,
  };
}

/** Build a fully-qualified mobile-service URL for a given cell. */
export function buildMobileUrl(cellId: string, path: string): string {
  return maybeLocalMobileUrl(buildCellServiceUrl(cellId, API_PREFIX, path));
}

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new MobileApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new MobileApiError('Missing access token.');
  }
}

export type MobileQuery = CellServiceQuery;

type MobileFetchArgs = {
  cellId: string;
  accessToken: string;
  /** Path under ``/mobile-service/v1`` (e.g. ``"/acquisitions"``). */
  path: string;
  query?: MobileQuery;
  signal?: AbortSignal;
  method?: 'GET' | 'POST';
  body?: unknown;
};

/** Generic GET/POST helper for mobile-service REST endpoints returning JSON typed as ``T``. */
export async function mobileFetch<T>(args: MobileFetchArgs): Promise<T> {
  const { cellId, path, query, signal, method = 'GET', body } = args;
  assertSession(cellId, args.accessToken);
  const url = appendQuery(buildMobileUrl(cellId, path), query);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    const timed = createTimedFetchSignal(signal);
    try {
      const res = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: timed.signal,
      });
      const text = await res.text();
      if (!res.ok) {
        throw new MobileApiError(
          `mobile-service ${path} returned ${res.status}`,
          res.status,
          text || null,
        );
      }
      if (!text) return null as T;
      try {
        return JSON.parse(text) as T;
      } catch {
        throw new MobileApiError(
          `mobile-service ${path} returned a non-JSON body`,
          res.status,
          text,
        );
      }
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) throw error;
      if (timed.wasTimedOut()) {
        throw new MobileTimeoutError(
          `mobile-service ${path} timed out after ${MOBILE_REQUEST_TIMEOUT_MS}ms`,
        );
      }
      if (error instanceof MobileApiError) throw error;
      throw new MobileApiError(
        `Network error calling mobile-service ${path}: ${(error as Error).message}`,
      );
    } finally {
      timed.cleanup();
    }
  });
}

/** Shape of a GraphQL error entry returned in a ``200`` body. */
export interface MobileGraphqlError {
  message: string;
  path?: (string | number)[];
  extensions?: Record<string, unknown>;
}

type MobileGraphqlArgs = {
  cellId: string;
  accessToken: string;
  /** Which vendor GraphQL endpoint to POST to. Defaults to ``mobile``. */
  endpoint?: MobileGraphqlEndpoint;
  query: string;
  variables?: Record<string, unknown>;
  signal?: AbortSignal;
};

/**
 * POST a GraphQL operation to mobile-service and return ``data`` typed as ``T``.
 *
 * GraphQL transports errors in a ``200`` body's ``errors`` array; this helper
 * throws a ``MobileApiError`` carrying the joined messages so callers (and
 * TanStack Query) treat them as failures.
 */
export async function mobileGraphql<T>(args: MobileGraphqlArgs): Promise<T> {
  const { cellId, endpoint = 'mobile', query, variables, signal } = args;
  assertSession(cellId, args.accessToken);
  const path = GRAPHQL_PATH_BY_ENDPOINT[endpoint];
  const url = buildMobileUrl(cellId, path);

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    const timed = createTimedFetchSignal(signal);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ query, variables: variables ?? {} }),
        signal: timed.signal,
      });
      const text = await res.text();
      if (!res.ok) {
        throw new MobileApiError(
          `mobile-service ${path} returned ${res.status}`,
          res.status,
          text || null,
        );
      }
      let parsed: { data?: T; errors?: MobileGraphqlError[] };
      try {
        parsed = text ? JSON.parse(text) : {};
      } catch {
        throw new MobileApiError(`mobile-service ${path} returned a non-JSON body`);
      }
      if (parsed.errors && parsed.errors.length > 0) {
        const message = parsed.errors.map((e) => e.message).join('; ');
        // The UI never shows `detail`, but the query is dynamic and these
        // messages are how you tell a bad `orderBy` from a missing field.
        console.error(`[mobile-service] ${path}: ${message}`);
        throw new MobileApiError(`GraphQL error: ${message}`, res.status, text);
      }
      return (parsed.data ?? null) as T;
    } catch (error) {
      if (isAbortError(error) && signal?.aborted) throw error;
      if (timed.wasTimedOut()) {
        throw new MobileTimeoutError(
          `mobile-service ${path} timed out after ${MOBILE_REQUEST_TIMEOUT_MS}ms`,
        );
      }
      if (error instanceof MobileApiError) throw error;
      throw new MobileApiError(
        `Network error calling mobile-service ${path}: ${(error as Error).message}`,
      );
    } finally {
      timed.cleanup();
    }
  });
}

// =============================================================================
// Domain types
// =============================================================================

/**
 * Acquisition vendor. GraphQL returns the enum names ``CELLEBRITE`` /
 * ``MAGNET`` (see ``AcquisitionTypeGql``); accept either casing for display.
 */
export type MobileAcquisitionType = 'CELLEBRITE' | 'MAGNET' | 'cellebrite' | 'magnet';

/**
 * One row in the acquisitions list, from the REST ``GET /acquisitions``
 * surface.
 *
 * This reads ``acquisition_map``, where ``case_name`` is non-null, rather than
 * the GraphQL ``acquisitions`` query, which inner-joins ``case_info`` and
 * returns its nullable ``case_name`` / ``case_number``. In practice most
 * acquisitions have no ``case_info`` row, so the GraphQL list both blanked the
 * case columns and dropped those acquisitions entirely.
 *
 * The trade-off is that ``acquisition_map`` carries no vendor, so
 * ``acquisitionType`` comes from the device-owner lookup instead.
 */
export interface MobileAcquisitionRow {
  mobileAcquisitionId: number;
  caseName: string;
  relativePath: string;
}

/**
 * One vendor-neutral device-owner profile (``commonDeviceOwners``). An
 * acquisition has one row per OS-level user profile on the device.
 */
export interface MobileDeviceOwner {
  mobileArtifactId: number;
  acquisitionType: MobileAcquisitionType;
  displayName: string | null;
  /** LLM-generated owner summary; absent until that pass has run. */
  llmOwner: string | null;
  phoneNumbers: string[];
  emails: string[];
  /** Best source tier that supplied a primary identity field; 1 is highest. */
  confidenceTier: number | null;
}

/** One artifact-type tally in an acquisition summary. */
export interface MobileAcquisitionStat {
  graphqlType: string;
  artifactName: string;
  count: number;
}

/** Full acquisition summary (type + per-artifact stats). */
export interface MobileAcquisitionSummary {
  mobileAcquisitionId: number;
  acquisitionType: MobileAcquisitionType;
  caseName: string | null;
  caseNumber: string | null;
  stats: MobileAcquisitionStat[];
}

/** Generic paginated connection returned by every artifact list query. */
export interface MobileConnection<TRow = Record<string, unknown>> {
  total: number;
  offset: number;
  limit: number;
  items: TRow[];
}

// =============================================================================
// UI config types (GET /mobile-service/v1/ui-config)
// =============================================================================

export interface MobileUiConfigField {
  name: string;
  type: string;
  /** Vendor-authored field heading from the artifact registry. */
  label?: string | null;
  /** Vendor-authored forensic meaning from the artifact registry. */
  description?: string | null;
  scalar: boolean;
  list: boolean;
  object: boolean;
  nullable: boolean;
}

export type MobileColumnRender =
  | 'text'
  | 'number'
  | 'datetime'
  | 'duration'
  | 'bool'
  | 'badge'
  | 'url'
  | 'list'
  | 'content'
  | 'party_list'
  | 'nested_table'
  | 'media_thumb'
  | 'avatar'
  | 'phone_number'
  | 'file_size'
  | 'coordinate'
  | 'direction';

export type MobileColumnFilter = 'string' | 'datetime' | 'number' | 'boolean' | 'enum';

export interface MobileColumnOverride {
  field: string;
  label?: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  render?: MobileColumnRender;
  sortable?: boolean;
  filter?: MobileColumnFilter;
  enum_values?: string[];
  hidden?: boolean;
}

export interface MobileNestedConfig {
  field: string;
  label?: string;
  graphql_type?: string;
  default_columns?: string[];
  column_overrides?: MobileColumnOverride[];
}

/** Base shape for a named presentation mode under ``front_end.views``. */
export interface MobileFrontEndView {
  name: string;
}

/** Table presentation (`name: "table"`). */
export interface MobileFrontEndTableView extends MobileFrontEndView {
  name: 'table';
  title?: string;
  table_only?: boolean;
  default_columns?: string[];
  column_overrides?: MobileColumnOverride[];
  content_fields?: string[];
  default_sort?: { field: string; direction: 'asc' | 'desc' };
  nested?: MobileNestedConfig[];
}

/** Direction strategy for the message-bubble view (`front_end.views[messages].direction`). */
export interface MobileMessageDirectionConfig {
  /** Boolean GraphQL path (dot-notation) that is true for owner-sent (outgoing) messages. */
  owner_field?: string;
  /** String GraphQL field whose value indicates direction. Pairs with `outgoing_values`. */
  field?: string;
  /** Values of `field` that mark an outgoing message; anything else is incoming. */
  outgoing_values?: string[];
}

/** Thread grouping strategy for the message-bubble view (`front_end.views[messages].thread`). */
export interface MobileMessageThreadConfig {
  mode: 'by_field' | 'by_parent';
  /** by_field: scalar GraphQL field identifying a message's thread (e.g. `chatId`). */
  key_field?: string;
  /**
   * by_field: derive the thread key from several fields combined order-independently
   * (e.g. `[sender, recipient]`) when there is no single thread id. Mutually
   * exclusive with `key_field`. Grouped and matched client-side.
   */
  key_fields?: string[];
  /** by_field: GraphQL field shown as the thread title when it is a real name (not a thread id). */
  title_field?: string;
  /** by_field: optional secondary GraphQL field shown under the title. */
  subtitle_field?: string;
  /** by_parent: GraphQL type of the thread (parent) artifact (e.g. `CellebriteChatGql`). */
  parent_graphql_type?: string;
  /** by_parent: GraphQL field on the message referencing its parent id (e.g. `chatArtifactId`). */
  parent_link_field?: string;
  /** by_parent: GraphQL field on the parent artifact used as the thread title when it is a real name. */
  parent_title_field?: string;
  /** by_parent: optional secondary GraphQL field on the parent artifact. */
  parent_subtitle_field?: string;
}

/**
 * One file source rendered inside a message bubble.
 *
 * `items_field` is a GraphQL object or list on the message. `media_field`,
 * `mime_field`, and `filename_field` are paths relative to each item.
 */
export interface MobileMessageAttachmentConfig {
  items_field: string;
  media_field: string;
  mime_field?: string;
  filename_field?: string;
}

/** Chat-style message presentation (`name: "messages"`). */
export interface MobileFrontEndBubbleMessageView extends MobileFrontEndView {
  name: 'messages';
  title?: string;
  body_field: string;
  timestamp_field: string;
  sender_name_field?: string;
  sender_id_field?: string;
  direction?: MobileMessageDirectionConfig;
  thread: MobileMessageThreadConfig;
  detail_fields?: string[];
  /** File sources shown inside the bubble. Absent on older ui-config. */
  attachments?: MobileMessageAttachmentConfig[];
}

/** Geospatial presentation (`name: "map"`). Plots each row's coordinate on a map. */
export interface MobileFrontEndMapView extends MobileFrontEndView {
  name: 'map';
  title?: string;
  /** GraphQL field with decimal latitude (WGS-84). Dot-notation allowed (e.g. `position.latitude`). */
  latitude_field: string;
  /** GraphQL field with decimal longitude (WGS-84). Dot-notation allowed (e.g. `position.longitude`). */
  longitude_field: string;
  /** Optional GraphQL field shown as the point's title in its popover. */
  label_field?: string;
  /** Optional GraphQL datetime field shown on each point's popover. */
  timestamp_field?: string;
  /** Ordered GraphQL fields shown in the point detail popover. Empty → label + timestamp only. */
  detail_fields?: string[];
}

/** Media-grid presentation (`name: "gallery"`) for artifacts carrying files. */
export interface MobileFrontEndGalleryView extends MobileFrontEndView {
  name: 'gallery';
  title?: string;
  /** GraphQL field with the full-size asset URL (e.g. `fileS3Url`). Dot-notation allowed. */
  media_field: string;
  /** Optional smaller preview URL. Omitted → the viewer sizes down `media_field`. */
  thumbnail_field?: string;
  /** Optional MIME type field; needed in practice when `media_kind` is `auto`. */
  mime_field?: string;
  /** `auto` resolves per row from `mime_field`, falling back to the URL extension. */
  media_kind?: 'image' | 'video' | 'audio' | 'document' | 'auto';
  /** Optional GraphQL field shown as the tile caption. */
  caption_field?: string;
  /** Optional datetime shown on the tile and used to order the grid. */
  timestamp_field?: string;
  /** Ordered fields shown in the lightbox detail panel. */
  detail_fields?: string[];
}

/** Chronological event-stream presentation (`name: "timeline"`). */
export interface MobileFrontEndTimelineView extends MobileFrontEndView {
  name: 'timeline';
  title?: string;
  /** GraphQL datetime field that positions a row on the timeline. */
  timestamp_field: string;
  /** Optional headline field; falls back to the artifact display name. */
  title_field?: string;
  /** Optional secondary line. */
  summary_field?: string;
  /** Optional field whose value splits entries into parallel lanes. */
  lane_field?: string;
  /** Optional icon for entries from this artifact in a merged timeline. */
  icon?: string;
  /** Ordered fields shown when an entry is expanded. Empty → all fields. */
  detail_fields?: string[];
}

/** Card-grid presentation (`name: "cards"`) for entity-like artifacts. */
export interface MobileFrontEndCardView extends MobileFrontEndView {
  name: 'cards';
  title?: string;
  /** GraphQL field shown as the card's primary label. */
  title_field: string;
  /** Optional field shown under the title. */
  subtitle_field?: string;
  /** Optional image URL for the avatar; falls back to initials from the title. */
  avatar_field?: string;
  /** Ordered fields rendered as small pills on the card. */
  badge_fields?: string[];
  /** Ordered fields listed in the card body. Empty → the artifact's default columns. */
  detail_fields?: string[];
}

/** One labelled group of fields in a record view. */
export interface MobileRecordSection {
  label: string;
  fields: string[];
}

/** A related artifact list surfaced on a record view. */
export interface MobileRecordRelation {
  field: string;
  label?: string;
  graphql_type?: string;
  display_fields?: string[];
}

/** Full-page detail presentation (`name: "record"`) for a single row. */
export interface MobileFrontEndRecordView extends MobileFrontEndView {
  name: 'record';
  title?: string;
  /** Optional heading field; falls back to the artifact display name plus its id. */
  title_field?: string;
  /** Ordered field groups. Empty → a flat list of every field. */
  sections?: MobileRecordSection[];
  /** Related lists rendered beneath the record's own fields. */
  related?: MobileRecordRelation[];
}

export type MobileFrontEndViewConfig =
  | MobileFrontEndTableView
  | MobileFrontEndBubbleMessageView
  | MobileFrontEndMapView
  | MobileFrontEndGalleryView
  | MobileFrontEndTimelineView
  | MobileFrontEndCardView
  | MobileFrontEndRecordView
  | MobileFrontEndView;

/**
 * Coarse cross-vendor grouping for an artifact type. Drives navigation grouping
 * and decides which types can merge into a combined timeline.
 */
export type MobileArtifactFamily =
  | 'message'
  | 'call'
  | 'contact'
  | 'location'
  | 'media'
  | 'web'
  | 'app'
  | 'account'
  | 'financial'
  | 'system';

/** Identity and grouping metadata for an artifact type (`front_end.meta`). */
export interface MobileFrontEndArtifactMeta {
  /** Icon name from the viewer's icon set (Phosphor), e.g. `PhoneCall`. */
  icon?: string;
  description?: string;
  family?: MobileArtifactFamily;
  /** Ordering hint within a family; higher sorts first. */
  priority?: number;
}

export interface MobileFrontEndConfig {
  graphql_root_query?: string;
  graphql_root_query_singular?: string;
  meta?: MobileFrontEndArtifactMeta | null;
  /** GraphQL text fields cross-artifact search matches. Empty → excluded from search. */
  searchable_fields?: string[];
  views?: MobileFrontEndViewConfig[];
}

/** One field a type's `groupBy` query accepts, in both spellings. */
export interface MobileGroupableField {
  /**
   * Field name this member groups under — both the key the group result carries
   * and the `where` key to filter on. Note this can differ from the type's
   * *selectable* field name: a field with a name override groups and filters as
   * `type` but selects as `deviceType`.
   */
  field: string;
  /** Enum member to pass to `groupBy(by:)`, verbatim (SCREAMING_SNAKE). */
  enum_value: string;
}

/**
 * What a type's list, `groupBy`, and `Aggregate` queries accept. Introspected
 * server-side, so it cannot drift from the live schema.
 */
export interface MobileUiConfigCapabilities {
  /** `where` key → the scalar it compares (e.g. `timeStamp` → `DateTime`). */
  filterable_fields?: Record<string, string>;
  groupable_fields?: MobileGroupableField[];
  /** Rollup name (`min`/`max`/`sum`/`avg`) → the columns it covers. */
  aggregate_fields?: Record<string, string[]>;
}

/** One named lat/lon pair authored on the artifact (`locations:`). */
export interface MobileArtifactLocation {
  name: string;
  latitude: string;
  longitude: string;
}

export interface MobileUiConfigArtifact {
  graphql_type: string;
  endpoint: MobileGraphqlEndpoint;
  category?: string | null;
  /** Named coordinate pairs (origin/destination on a trip, or a single point). */
  locations?: MobileArtifactLocation[];
  /** Magnet sub-group within a category (e.g. ``whatsapp``). */
  collection?: string | null;
  /** Magnet application/module grouping (the leaf of ``collection_name``). */
  app?: string | null;
  /** Platforms on which Magnet documents this artifact. */
  platforms?: string[] | null;
  /** Vendor-documented Magnet recovery methods. */
  recovery_method?: string[] | null;
  table_name?: string | null;
  /** Human-readable label from ``front_end.views[table].title`` when authored. */
  title?: string | null;
  ufdr_type?: string | null;
  has_parties?: boolean;
  fields: MobileUiConfigField[];
  front_end?: MobileFrontEndConfig | null;
  /** Introspected filter / groupBy / aggregate capabilities. Absent when the type has none. */
  capabilities?: MobileUiConfigCapabilities | null;
}

export interface MobileUiConfigResponse {
  version: string;
  artifacts: Record<string, MobileUiConfigArtifact>;
}

// =============================================================================
// GraphQL query builders + typed callers
// =============================================================================

const ACQUISITION_SUMMARY_QUERY = /* GraphQL */ `
  query MobileAcquisitionSummary($mobileAcquisitionId: Int!) {
    acquisition(mobileAcquisitionId: $mobileAcquisitionId) {
      mobileAcquisitionId
      acquisitionType
      caseName
      caseNumber
      stats {
        graphqlType
        artifactName
        count
      }
    }
  }
`;

/** Fetch one page of acquisitions for the current tenant. */
export async function fetchMobileAcquisitionPage(args: {
  cellId: string;
  accessToken: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<MobileConnection<MobileAcquisitionRow>> {
  const { cellId, accessToken, offset = 0, limit = 100, signal } = args;
  const body = await mobileFetch<MobileConnection<MobileAcquisitionRow> | null>({
    cellId,
    accessToken,
    path: '/acquisitions',
    query: { offset, limit },
    signal,
  });
  return {
    items: Array.isArray(body?.items) ? body.items : [],
    total: body?.total ?? 0,
    offset: body?.offset ?? offset,
    limit: body?.limit ?? limit,
  };
}

/**
 * ``acquisition_map`` page size and page cap used to resolve one acquisition's
 * case name. REST ``GET /acquisitions`` has no by-id filter, so the row is found
 * by scanning; the cap keeps a tenant with a very large map from turning one
 * lookup into an unbounded number of round trips.
 */
const CASE_NAME_SCAN_LIMIT = 500;
const CASE_NAME_SCAN_MAX_PAGES = 10;

/**
 * Resolve one acquisition's case name from ``acquisition_map`` over REST.
 *
 * The GraphQL summary's ``caseName`` comes from the nullable ``case_info`` join
 * and is null for most acquisitions, so anything that needs a real case name
 * (the assistant scopes chat history by it) has to read ``acquisition_map``.
 * Returns null when the acquisition isn't found within the scan cap or its row
 * carries no case name, so callers can degrade instead of failing.
 */
export async function fetchMobileAcquisitionCaseName(args: {
  cellId: string;
  accessToken: string;
  mobileAcquisitionId: number;
  signal?: AbortSignal;
}): Promise<string | null> {
  const { cellId, accessToken, mobileAcquisitionId, signal } = args;

  for (let page = 0; page < CASE_NAME_SCAN_MAX_PAGES; page += 1) {
    const offset = page * CASE_NAME_SCAN_LIMIT;
    const body = await fetchMobileAcquisitionPage({
      cellId,
      accessToken,
      offset,
      limit: CASE_NAME_SCAN_LIMIT,
      signal,
    });

    const match = body.items.find((row) => row.mobileAcquisitionId === mobileAcquisitionId);
    if (match) return match.caseName?.trim() || null;

    // A short page means the end of the map. ``total`` is only trusted when
    // present, since it reads as 0 when the endpoint omits it.
    const shortPage = body.items.length < CASE_NAME_SCAN_LIMIT;
    const reachedTotal = body.total > 0 && offset + body.items.length >= body.total;
    if (shortPage || reachedTotal) break;
  }

  return null;
}

/**
 * One ``acquisition_map`` row, including the path relative to the case.
 *
 * ``GET /acquisitions/{id}`` is the by-id read. A 404 is "not in this tenant"
 * and comes back as null so the overview can show an empty state.
 */
export async function fetchMobileAcquisition(args: {
  cellId: string;
  accessToken: string;
  mobileAcquisitionId: number;
  signal?: AbortSignal;
}): Promise<MobileAcquisitionRow | null> {
  const { cellId, accessToken, mobileAcquisitionId, signal } = args;
  try {
    return await mobileFetch<MobileAcquisitionRow>({
      cellId,
      accessToken,
      path: `/acquisitions/${mobileAcquisitionId}`,
      signal,
    });
  } catch (error) {
    if (error instanceof MobileApiError && error.status === 404) return null;
    throw error;
  }
}

/** Fetch one acquisition's summary (type + stats). */
export async function fetchMobileAcquisitionSummary(args: {
  cellId: string;
  accessToken: string;
  mobileAcquisitionId: number;
  signal?: AbortSignal;
}): Promise<MobileAcquisitionSummary | null> {
  const { cellId, accessToken, mobileAcquisitionId, signal } = args;
  const data = await mobileGraphql<{ acquisition: MobileAcquisitionSummary | null }>({
    cellId,
    accessToken,
    endpoint: 'mobile',
    query: ACQUISITION_SUMMARY_QUERY,
    variables: { mobileAcquisitionId },
    signal,
  });
  return data.acquisition;
}

/** Fetch the runtime UI configuration document (cached; keyed by version/ETag). */
export async function fetchMobileUiConfig(args: {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
}): Promise<MobileUiConfigResponse> {
  const { cellId, accessToken, signal } = args;
  return mobileFetch<MobileUiConfigResponse>({
    cellId,
    accessToken,
    path: '/ui-config',
    signal,
  });
}

/** Locator posted as-is from a chat citation ``evidence`` object. */
export type HydrateEvidenceLocator = {
  kind: string;
  mobileAcquisitionId: number;
  [key: string]: unknown;
};

/**
 * Fields ``POST /evidence/hydrate`` accepts. Anything else on a stored
 * citation stays off the wire. Matches mobile-service ``EvidenceLocator``.
 */
const HYDRATE_LOCATOR_KEYS = [
  'id',
  'kind',
  'view',
  'requestedView',
  'mobileAcquisitionId',
  'queryName',
  'artifactType',
  'graphqlType',
  'endpoint',
  'mobileArtifactId',
  'mobileArtifactIds',
  'highlightedArtifactIds',
  'where',
  'orderBy',
  'by',
  'having',
  'orderByCount',
  'limit',
  'offset',
  'queryShape',
  'resultCount',
  'fields',
  'aggregates',
  'mobileThreadId',
  'chatArtifactId',
  'mobileFileId',
  'mobileFilePathId',
  'resource',
  'window',
  'typesChecked',
  'label',
  'displayFields',
] as const;

/** Copy only the hydrate locator fields. Unknown citation keys are dropped. */
export function hydrateLocatorBody(
  locator: HydrateEvidenceLocator,
  requestedView?: string,
): HydrateEvidenceLocator {
  const body: HydrateEvidenceLocator = {
    kind: locator.kind,
    mobileAcquisitionId: locator.mobileAcquisitionId,
  };
  for (const key of HYDRATE_LOCATOR_KEYS) {
    if (key === 'kind' || key === 'mobileAcquisitionId') continue;
    if (locator[key] !== undefined) body[key] = locator[key];
  }
  if (requestedView) body.requestedView = requestedView;
  return body;
}

/** How the hydrate payload should be rendered. */
export type HydrateDisplay = 'json' | 'markdown' | 'thread' | 'map';

export type HydrateEvidenceFocus = {
  mode?: 'items' | 'range';
  mobileArtifactId?: number | null;
  mobileArtifactIds?: number[];
  highlightedArtifactIds?: number[];
  mobileThreadId?: number | null;
  chatArtifactId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type HydrateEvidenceWindow = {
  kind: 'thread' | 'timeRange' | 'group';
  citedCount: number;
  field?: string | null;
  start?: string | null;
  end?: string | null;
  contextBefore?: number;
  contextAfter?: number;
};

/** Envelope from ``POST /evidence/hydrate``. */
export type HydrateEvidenceResponse = {
  display: HydrateDisplay;
  locator: HydrateEvidenceLocator;
  items: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  view?: string | null;
  availableViews?: string[];
  focus?: HydrateEvidenceFocus | null;
  window?: HydrateEvidenceWindow | null;
};

/** Hydrate a chat citation locator into artifact rows. */
export async function hydrateEvidence(args: {
  cellId: string;
  accessToken: string;
  locator: HydrateEvidenceLocator;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<HydrateEvidenceResponse> {
  const { cellId, accessToken, locator, offset, limit, signal } = args;
  const query: MobileQuery = {};
  if (offset != null) query.offset = offset;
  if (limit != null) query.limit = limit;
  return mobileFetch<HydrateEvidenceResponse>({
    cellId,
    accessToken,
    path: '/evidence/hydrate',
    method: 'POST',
    query: Object.keys(query).length > 0 ? query : undefined,
    body: hydrateLocatorBody(locator),
    signal,
  });
}

// =============================================================================
// Query keys
// =============================================================================

/**
 * Hierarchical TanStack Query key factory for mobile-service resources. Use the
 * most specific key per hook so partial invalidation stays straightforward.
 */
export const mobileKeys = {
  all: ['mobile'] as const,

  uiConfig: (cellId: string | null) => [...mobileKeys.all, 'ui-config', cellId] as const,

  acquisitions: () => [...mobileKeys.all, 'acquisitions'] as const,
  acquisitionList: (cellId: string | null, params: { offset: number; limit: number }) =>
    [...mobileKeys.acquisitions(), 'list', cellId, params] as const,
  acquisitionSummary: (cellId: string | null, mobileAcquisitionId: number) =>
    [...mobileKeys.acquisitions(), 'summary', cellId, mobileAcquisitionId] as const,
  acquisitionCaseName: (cellId: string | null, mobileAcquisitionId: number) =>
    [...mobileKeys.acquisitions(), 'case-name', cellId, mobileAcquisitionId] as const,
  acquisitionMap: (cellId: string | null, mobileAcquisitionId: number) =>
    [...mobileKeys.acquisitions(), 'map-row', cellId, mobileAcquisitionId] as const,
  acquisitionDeviceOwners: (cellId: string | null, mobileAcquisitionIds: readonly number[]) =>
    [...mobileKeys.acquisitions(), 'device-owners', cellId, mobileAcquisitionIds] as const,

  evidence: () => [...mobileKeys.all, 'evidence'] as const,
  evidenceHydrate: (cellId: string | null, locator: Record<string, unknown>) =>
    [...mobileKeys.evidence(), 'hydrate', cellId, locator] as const,

  artifacts: () => [...mobileKeys.all, 'artifacts'] as const,
  artifactList: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    params: Record<string, unknown>,
  ) =>
    [
      ...mobileKeys.artifacts(),
      'list',
      cellId,
      mobileAcquisitionId,
      graphqlType,
      params,
    ] as const,

  messages: () => [...mobileKeys.all, 'messages'] as const,
  messageThreads: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    params: Record<string, unknown>,
  ) =>
    [
      ...mobileKeys.messages(),
      'threads',
      cellId,
      mobileAcquisitionId,
      graphqlType,
      params,
    ] as const,
  threadMessages: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    threadKey: string,
    params: Record<string, unknown>,
  ) =>
    [
      ...mobileKeys.messages(),
      'thread',
      cellId,
      mobileAcquisitionId,
      graphqlType,
      threadKey,
      params,
    ] as const,

  map: () => [...mobileKeys.all, 'map'] as const,
  mapPoints: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    whereKey: string,
  ) =>
    [...mobileKeys.map(), 'points', cellId, mobileAcquisitionId, graphqlType, whereKey] as const,

  artifactRecord: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    recordId: string,
  ) =>
    [
      ...mobileKeys.artifacts(),
      'record',
      cellId,
      mobileAcquisitionId,
      graphqlType,
      recordId,
    ] as const,

  facets: () => [...mobileKeys.all, 'facets'] as const,
  artifactFacets: (
    cellId: string | null,
    mobileAcquisitionId: number,
    graphqlType: string,
    params: Record<string, unknown>,
  ) =>
    [
      ...mobileKeys.facets(),
      cellId,
      mobileAcquisitionId,
      graphqlType,
      params,
    ] as const,

  playbooks: (cellId: string | null) => [...mobileKeys.all, 'playbooks', cellId] as const,

  search: () => [...mobileKeys.all, 'search'] as const,
  globalSearch: (
    cellId: string | null,
    mobileAcquisitionId: number,
    params: Record<string, unknown>,
  ) => [...mobileKeys.search(), cellId, mobileAcquisitionId, params] as const,
};

// =============================================================================
// Acquisition-map resolution (assistant chat scope → mobileAcquisitionIds)
// =============================================================================
//
// Used by the assistant chat deep-dive path to attach ``mobileAcquisitionIds``
// when the user scopes a suspect's phone. Uses the REST ``GET /acquisitions``
// surface (which exposes ``relativePath``) rather than the GraphQL acquisitions
// query, so scoped evidence paths can be matched to acquisition ids.

/**
 * Substrings that mark a scoped evidence path as mobile/UFDR data. Mirrors the
 * server-side gate in assistant-service so we only hit mobile-service when the
 * user actually scoped a phone extraction.
 */
const MOBILE_PATH_HINTS = ['ufdr', 'mobile_processing', 'cellebrite', 'magnet'];

/** One ``acquisition_map`` row from the REST ``GET /acquisitions`` surface. */
export type MobileAcquisitionMapRow = MobileAcquisitionRow;

export function isMobileEvidencePath(path: string): boolean {
  const lower = path.toLowerCase();
  return MOBILE_PATH_HINTS.some((hint) => lower.includes(hint));
}

function normalizeScopePath(path: string): string {
  return path.trim().replace(/^\/+/, '').toLowerCase();
}

/**
 * True when one path is the other, or sits inside it on a segment boundary.
 * Raw substring and basename checks are rejected: a short scope or a shared
 * filename would pin the wrong acquisition.
 */
export function mobileScopePathsAlign(scopedPath: string, relativePath: string): boolean {
  const scoped = normalizeScopePath(scopedPath);
  const relative = normalizeScopePath(relativePath);
  if (!scoped || !relative) return false;
  if (scoped === relative) return true;
  if (scoped.startsWith(`${relative}/`)) return true;
  return relative.startsWith(`${scoped}/`);
}

/**
 * One analysis playbook mobile-service ships for the assistant's mobile agent.
 * Listed by the viewer to seed one-click prompts.
 */
export interface MobilePlaybookSummary {
  slug: string;
  title: string;
  summary: string;
  chapter_count: number;
}

/** List the packaged analysis playbooks (``GET /v1/playbooks``). */
export async function fetchMobilePlaybooks(args: {
  cellId: string;
  accessToken: string;
  signal?: AbortSignal;
}): Promise<MobilePlaybookSummary[]> {
  const body = await mobileFetch<MobilePlaybookSummary[] | null>({
    cellId: args.cellId,
    accessToken: args.accessToken,
    path: '/playbooks',
    signal: args.signal,
  });
  return Array.isArray(body) ? body : [];
}

type AcquisitionMapScan = {
  rows: MobileAcquisitionMapRow[];
  /** False when the page cap was hit before the map ended. */
  complete: boolean;
};

/**
 * Page ``acquisition_map`` the same way case-name lookup does. A single page of
 * 500 can hide a second match and make a partial scan look unique.
 */
async function scanMobileAcquisitionMap(args: {
  cellId: string;
  accessToken: string;
  caseName?: string | null;
  signal?: AbortSignal;
}): Promise<AcquisitionMapScan> {
  const { cellId, accessToken, caseName, signal } = args;
  if (!cellId || !accessToken) return { rows: [], complete: true };

  const rows: MobileAcquisitionMapRow[] = [];
  for (let page = 0; page < CASE_NAME_SCAN_MAX_PAGES; page += 1) {
    const offset = page * CASE_NAME_SCAN_LIMIT;
    const query: MobileQuery = { offset, limit: CASE_NAME_SCAN_LIMIT };
    if (caseName) query.caseName = caseName;
    const body = await mobileFetch<{
      items?: MobileAcquisitionMapRow[];
      total?: number;
    }>({
      cellId,
      accessToken,
      path: '/acquisitions',
      query,
      signal,
    });
    const items = Array.isArray(body?.items) ? body.items : [];
    rows.push(...items);

    const total = body?.total ?? 0;
    const shortPage = items.length < CASE_NAME_SCAN_LIMIT;
    const reachedTotal = total > 0 && offset + items.length >= total;
    if (shortPage || reachedTotal) return { rows, complete: true };
  }

  return { rows, complete: false };
}

/** List ``acquisition_map`` rows (with ``relativePath``) for a case via REST. */
export async function fetchMobileAcquisitionMap(args: {
  cellId: string;
  accessToken: string;
  caseName?: string | null;
  signal?: AbortSignal;
}): Promise<MobileAcquisitionMapRow[]> {
  const scanned = await scanMobileAcquisitionMap(args);
  return scanned.rows;
}

/** Match acquisition-map rows to scoped paths on a path-segment boundary. */
export function matchMobileAcquisitionIds(
  rows: MobileAcquisitionMapRow[],
  scopedPaths: string[],
): number[] {
  const scoped = scopedPaths.map(normalizeScopePath).filter(Boolean);
  const ids = new Set<number>();
  for (const row of rows) {
    const relative = row.relativePath ?? '';
    if (!normalizeScopePath(relative)) continue;
    if (scoped.some((path) => mobileScopePathsAlign(path, relative))) {
      ids.add(row.mobileAcquisitionId);
    }
  }
  return [...ids];
}

/**
 * Outcome of pinning a TimePilot Mobile question to one acquisition.
 *
 * ``unscoped`` means the user did not select a UFDR, Cellebrite, or Magnet
 * path, so the caller may omit ``mobileAcquisitionId`` and let the server
 * analyze the case. Every other non-unique outcome is a refusal: sending the
 * question without an id would silently widen a scoped phone to the whole case.
 */
export type MobileAcquisitionResolution =
  | { status: 'unscoped' }
  | { status: 'unique'; id: number }
  | { status: 'unmatched' }
  | { status: 'ambiguous' }
  | { status: 'incomplete' }
  | { status: 'failed' };

const MOBILE_SCOPE_REFUSAL: Record<
  Exclude<MobileAcquisitionResolution['status'], 'unscoped' | 'unique'>,
  string
> = {
  unmatched:
    'No phone extraction matches the selected evidence. Choose a single UFDR, Cellebrite, or Magnet file and try again.',
  ambiguous:
    'More than one phone extraction matches the selected evidence. Narrow the scope to a single extraction.',
  incomplete:
    'The phone extraction list is too large to confirm a single match. Narrow the scope and try again.',
  failed: 'The phone extraction could not be confirmed. Try again.',
};

/** Whether a mobile submit may proceed, and the id to attach when one was pinned. */
export type MobileAcquisitionSubmit =
  | { ok: true; mobileAcquisitionId: number | null }
  | { ok: false; error: string };

/**
 * Decide the submit. A unique match sends that id. No mobile path omits the
 * id. Any other resolution refuses the send.
 */
export function mobileAcquisitionIdForSubmit(
  resolution: MobileAcquisitionResolution,
): MobileAcquisitionSubmit {
  if (resolution.status === 'unscoped') return { ok: true, mobileAcquisitionId: null };
  if (resolution.status === 'unique') return { ok: true, mobileAcquisitionId: resolution.id };
  return { ok: false, error: MOBILE_SCOPE_REFUSAL[resolution.status] };
}

/**
 * Pin a TimePilot Mobile question to the acquisition that owns the scoped
 * evidence.
 *
 * An incomplete scan must not yield an id: a later page may hold another
 * match, and a single id from a partial list would pin the wrong phone.
 *
 * ``caseFolderName`` is the case's **S3 folder segment**, not its display name.
 * ``acquisition_map.case_name`` reads like a name but the loader fills it by
 * splitting the bulk key (``parse_bulk_case_and_relative_path``), so it holds the
 * folder segment — a ``folder_token`` UUID for any case that has one — and
 * mobile-service matches it with bare equality. Passing the display name returned
 * zero rows for a UUID-folder case, which looked like a completed scan of an empty
 * map and refused every scoped Mobile submit (TR-359).
 *
 * When the segment has not resolved, this falls back to ``caseDisplayName`` and
 * only refuses if neither is available. The fallback can only ever *narrow*: for
 * a legacy case the display name IS the segment, so the submit keeps working; for
 * a UUID-folder case it matches nothing and the caller is refused as
 * ``unmatched`` — no worse than before. Case names are unique per tenant and a
 * segment is either a UUID or a name, so it cannot match the wrong case.
 *
 * What it must never do is drop the filter: omitting ``caseName`` scans the whole
 * tenant, and the match then rests on ``relativePath`` alone — so two cases
 * holding ``Manual/phone.ufdr`` would cross-match and could pin the question to a
 * *different* case's phone.
 */
export async function resolveMobileAcquisitionIds(args: {
  cellId: string;
  accessToken: string;
  idToken?: string | null;
  caseFolderName?: string | null;
  /** Display name, used only as the narrowing fallback described above. */
  caseDisplayName?: string | null;
  scopedPaths: string[];
  signal?: AbortSignal;
}): Promise<MobileAcquisitionResolution> {
  const mobileScoped = args.scopedPaths.filter(isMobileEvidencePath);
  // Checked before the segment: with no mobile evidence scoped there is nothing
  // to pin, so the caller needs no case filter and must not be refused.
  if (mobileScoped.length === 0) return { status: 'unscoped' };
  const caseFilter = args.caseFolderName?.trim() || args.caseDisplayName?.trim();
  if (!caseFilter) return { status: 'failed' };
  try {
    const scanned = await scanMobileAcquisitionMap({ ...args, caseName: caseFilter });
    if (!scanned.complete) return { status: 'incomplete' };
    const ids = matchMobileAcquisitionIds(scanned.rows, mobileScoped);
    if (ids.length === 1) return { status: 'unique', id: ids[0] };
    if (ids.length === 0) return { status: 'unmatched' };
    return { status: 'ambiguous' };
  } catch {
    return { status: 'failed' };
  }
}
